import {CSSProperties, FormEvent} from "react";
import {compose} from "redux";
import {connect} from "react-redux";
import {createSelector} from "reselect";
import AppBar from "material-ui/AppBar";
import {Table, TableBody, TableRow, TableRowColumn} from "material-ui/Table";
import FloatingActionButton from "material-ui/FloatingActionButton";
import FontIcon from "material-ui/FontIcon";
import Dialog from "material-ui/Dialog";
import FlatButton from "material-ui/FlatButton";
import TextField from "material-ui/TextField";
import Subheader from "material-ui/Subheader";

import {Ganymede} from "@slices/Ganymede/ClientManager";
import {RootState} from "src/reducers/index";
import {routeComponent, InjectedProps} from "src/utils/router";
import LayoutWithAppBar from "src/components/LayoutWithAppBar";
import TokenField from "./TokenField";

import {
  projectRoute, createClientClicked,
  cancelCreateClicked, newClientNameChanged, createClientSubmitted,
  clientClicked, clientDialogClosed, generateTokenClicked,
} from "./Project.actions";


const styles = {
  createButton: {
    position: 'fixed',
    top: 30,
    right: 28,
    zIndex: 2000,
  },

  tableRow: {
    cursor: 'pointer',
  },

  createDialog: {
    width: 360,
  },

  identityInput: {
    fontFamily: 'monospace',
  },

  tokenContainer: {
    display: 'flex',
    alignItems: 'baseline',
  },
};


interface ClientsTableProps {
  clients: Ganymede.Client[];
  onRowClick(row: number): void;
}

const ClientsTable = ({
  clients,
  onRowClick,
}: ClientsTableProps) => {
  return <Table selectable={false} onCellClick={onRowClick}>
    <TableBody displayRowCheckbox={false}>
      {clients.map(client => <TableRow key={client.name}
                                       style={styles.tableRow}>
        <TableRowColumn>
          {client.name}
        </TableRowColumn>
        <TableRowColumn>
          <FontIcon className="material-icons">settings</FontIcon>
        </TableRowColumn>
      </TableRow>)}
    </TableBody>
  </Table>;
};


type ProjectProps = InjectedProps<{style?: CSSProperties}, {}>;

const Project = compose(
  routeComponent<{style?: CSSProperties}, {}>(projectRoute),
  connect(
    createSelector(
      createSelector(
        (state: RootState, props: ProjectProps) => (props.params as any).id,
        (state: RootState) => state.routes.projects.projects,
        (projectId, projects) => {
          const project = projects == null ? null
            : projects.find(project => project.id === projectId);

          return project;
        },
      ),
      (state: RootState) => state.routes.project,
      (project, projectState) => Object.assign({project}, projectState),
    ),
    {
      handleCreateClick() {
        return createClientClicked();
      },
      handleCancelCreateClick() {
        return cancelCreateClicked();
      },
      handleClientNameChange(e: any, value: string) {
        return newClientNameChanged(value);
      },
      handleClientSubmit(e: FormEvent<any>) {
        e.preventDefault();
        return createClientSubmitted();
      },
      handleClientClick(row: number) {
        return clientClicked({row});
      },
      handleClientDialogClose() {
        return clientDialogClosed();
      },
      handleGenerateTokenClick() {
        return generateTokenClicked();
      },
    },
  ),
)(({
  style,
  project,

  clients,
  createDialogOpen,
  isCreating,
  newClientName,
  selectedClientRow,
  clientToken,

  handleCreateClick,
  handleCancelCreateClick,
  handleClientNameChange,
  handleClientSubmit,
  handleClientClick,
  handleClientDialogClose,
  handleGenerateTokenClick,
}) => {
  const appBar = <AppBar title={`Project ${project && project.name}`}
                         showMenuIconButton={false}/>;

  const createDialogActions = [
    <FlatButton label="Cancel"
                disabled={isCreating}
                onTouchTap={handleCancelCreateClick}/>,
    <FlatButton label="Create"
                disabled={isCreating}
                onTouchTap={handleClientSubmit}/>,
  ];

  const createDialog = <Dialog title="Create Client"
                         open={createDialogOpen}
                         actions={createDialogActions}
                         contentStyle={styles.createDialog}>
    <form onSubmit={handleClientSubmit}>
      <TextField fullWidth name="name"
                 hintText="Client name"
                 disabled={isCreating}
                 value={newClientName}
                 onChange={handleClientNameChange}/>
    </form>
  </Dialog>;

  const selectedClient = clients == null || selectedClientRow == null ? null
    : clients[selectedClientRow];

  const clientIdentity = selectedClient == null
    ? ''
    : `Client|${selectedClient.projectId}|${selectedClient.name}`;

  const clientDialog = <Dialog title={selectedClient && selectedClient.name}
                               open={selectedClientRow != null}
                               onRequestClose={handleClientDialogClose}>
    <TextField fullWidth
               floatingLabelText="Identity"
               readOnly
               style={styles.identityInput}
               value={clientIdentity}/>

    <TokenField style={styles.tokenContainer}
                clientToken={clientToken}
                onTokenGenerateButtonClick={handleGenerateTokenClick}/>
  </Dialog>;

  return <LayoutWithAppBar style={style} appBarElement={appBar}>
    <div>
      <FloatingActionButton style={styles.createButton} secondary
                            onTouchTap={handleCreateClick}>
        <FontIcon className="material-icons">add</FontIcon>
      </FloatingActionButton>

      {createDialog}
      {clientDialog}

      <Subheader>Clients</Subheader>

      {clients == null ? <div>Loading...</div> :
        clients.length === 0 ? <div>No clients</div> :
          <ClientsTable clients={clients} onRowClick={handleClientClick}/>}
    </div>
  </LayoutWithAppBar>;
});

export default Project;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/routes/projects/project/Project.tsx